import { HTTP_API_V2 } from '../../http-common'

// Reference broiler growth curves live in the v2 API
// (GET /breed-growth-curves). The dataset is small (a handful of rows), so it is
// fetched once and cached. Each curve is shaped as
// { id, strain, sex, label, source, source_url, source_page, max_age,
// weights: number[], fcr: number[] } where `weights` is indexed by age in days
// (index 0 = day-old chick) and holds the target live body weight in grams, and
// `fcr` is the age-indexed cumulative target feed conversion ratio (0 = no
// reference published for that age).
export default {
  state: {
    loadedBreedCurves: [],
    breedCurvesLoaded: false
  },
  getters: {
    loadedBreedCurves (state) {
      return state.loadedBreedCurves
    },
    // Resolves the best matching curve for a given strain and sex, applying the
    // agreed fallbacks: requested sex -> as_hatched -> any sex of the strain;
    // and, when no strain is informed, defaults to Cobb 500 (the most common in
    // Brazil). Returns null only when no curves are loaded at all.
    breedCurveFor: (state) => ({ strain, sex } = {}) => {
      const curves = state.loadedBreedCurves
      if (!curves.length) {
        return null
      }
      const targetStrain = strain || 'cobb500'
      const strainCurves = curves.filter(curve => curve.strain === targetStrain)
      const pool = strainCurves.length
        ? strainCurves
        : curves.filter(curve => curve.strain === 'cobb500')
      const searchPool = pool.length ? pool : curves
      return (
        searchPool.find(curve => curve.sex === sex) ||
        searchPool.find(curve => curve.sex === 'as_hatched') ||
        searchPool[0] ||
        null
      )
    }
  },
  mutations: {
    setLoadedBreedCurves (state, payload) {
      state.loadedBreedCurves = Array.isArray(payload) ? payload : []
      state.breedCurvesLoaded = true
    }
  },
  actions: {
    loadBreedCurves ({ commit, state }, force = false) {
      if (state.breedCurvesLoaded && !force) {
        return Promise.resolve(state.loadedBreedCurves)
      }
      return HTTP_API_V2.get('breed-growth-curves')
        .then(result => {
          const curves = Array.isArray(result.data) ? result.data : []
          commit('setLoadedBreedCurves', curves)
          return curves
        })
        .catch(err => {
          console.log('error loading breed growth curves: ', err)
          commit('setLoadedBreedCurves', [])
          return []
        })
    },
    // Requests the weight projection from the API. The projection method is
    // proprietary and computed server-side; the weighings, genetics, sex and
    // reference curve are all sourced from the backend database, so the client
    // only identifies the batch and the horizon and receives the resulting
    // points ({ standard, genetics, usesCurve }). Not cached: it depends on
    // live data.
    computeWeightProjection (context, { batchId, days, currentAge } = {}) {
      const emptyProjection = { standard: [], genetics: [], usesCurve: false }
      if (!batchId || !days) {
        return Promise.resolve(emptyProjection)
      }
      const payload = { batchId, days }
      if (currentAge !== null && currentAge !== undefined) {
        payload.currentAge = currentAge
      }
      return HTTP_API_V2.post('bird-weight/projection', payload)
        .then(result => {
          const data = result.data || {}
          return {
            standard: Array.isArray(data.standard) ? data.standard : [],
            genetics: Array.isArray(data.genetics) ? data.genetics : [],
            usesCurve: !!data.usesCurve
          }
        })
        .catch(err => {
          console.log('error computing weight projection: ', err)
          return emptyProjection
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/breedCurves/index.js